import { makeStyles } from 'tss-react/mui';
import React, { lazy, Suspense, useState } from 'react';
import { useTranslation } from 'react-i18next/';
import ApiButton from 'web-library/stories/atom/Button/ApiButton';
import Box from 'web-library/stories/atom/Box/Box';
import ErrorInfo from 'web-library/stories/molecule/ErrorInfo/ErrorInfo';
import ControlledDatePicker from 'web-library/stories/atom/DatePicker/ControlledDatePicker';
import Typography from 'web-library/stories/atom/Typography/Typography';
import TextFieldForm from 'web-library/stories/atom/TextField/TextFieldForm';
import { useGetCaptchaSiteKey } from '../../../api-refactored/user/useGetCaptchaSiteKey';
import { usePostCaptchaVerifyToken } from '../../../api-refactored/user/usePostCaptchaVerifyToken';
import { useForgotPasswordForm } from '../logic/useForgotPasswordForm';

import {typeTheme} from "web-library/theme/helper/tssHelper";
const Captcha = lazy(() => import('web-library/stories/atom/Captcha/Captcha'));

const useStyles = makeStyles()((theme) =>
({
  formContainer: {
    marginBottom: theme.spacing(7),
  },
  input: {
    marginBottom: theme.spacing(3),
    [theme.breakpoints.down('sm')]: {
      marginBottom: theme.spacing(2),
    },
  },
  errorMessage: {
    color: theme.color.state.stateError,
    marginBottom: theme.spacing(2),
  },
  captcha: {
    paddingTop: theme.spacing(3),
  },
  successMessage: {
     ...typeTheme(theme, 'helperText'),
    color: theme.color.state.stateSuccess,
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(2),
  },
}));

const ForgotPasswordForm = () => {
  const { classes } = useStyles();

  const {
    forgotPasswordMutation,
    generateResetLink,
    clearInfoText,
    form,
    infoText,
    showCaptcha,
    showSuccess,
  } = useForgotPasswordForm();
  const { control, handleSubmit, formState: { errors }, setValue } = form;
  const { t } = useTranslation();

  const [isBot, setIsBot] = useState<boolean>(false);
  const checkIsBot = (result: boolean) => setIsBot(result);
  const captchaVerifyTokenMutation = usePostCaptchaVerifyToken({
    onSuccess: (data) => setIsBot(data ? data.isBot : true),
    onError: () => setIsBot(true),
  });

  return (
    <Box className={classes.formContainer}>
      <form onSubmit={handleSubmit(generateResetLink)}>
        <TextFieldForm
            form={form}
          label={t('profile->>ForgotPasswordForm.Username')}
          placeholder="Enter username"
          isRequired
          name="username"
          type="text"
          error={!!errors.username}
          helperText={errors?.username?.message?.toString()}
          className={classes.input}
        />
        <ControlledDatePicker
          id="dateOfBirth"
          label="Date of Birth"
          isRequired
          onChange={(date) => {
            setValue('dateOfBirth', date || '');
          }}
          inputVariant="filled"
          name="dateOfBirth"
          control={control}
          error={!!errors.dateOfBirth}
          helperText={errors?.dateOfBirth?.message?.toString()}
          disableFuture
        />

        {showCaptcha && (
          <Suspense fallback={null}>
            <Captcha
              {...{
                id: 'forgot-password-captcha',
                className: classes.captcha,
                checkIsBot,
                captchaSiteKeyMutation: useGetCaptchaSiteKey,
                captchaVerifyTokenMutation,
              }}
            />
          </Suspense>
        )}
        {showSuccess ? (
          <Typography className={classes.successMessage} id="successText-resetPassword">
            {infoText}
          </Typography>
        ) : (
          <ErrorInfo
            className={classes.errorMessage}
            id="errorText-resetPassword"
            label={infoText || ''}
          />
        )}
        <ApiButton
          id="button-forgotPassword-resetPassword"
          status={forgotPasswordMutation.status}
          type="submit"
          onClick={clearInfoText}
          disabled={isBot}
        >
          {t('profile->>ForgotPasswordForm.Reset Password')}
        </ApiButton>
      </form>
    </Box>
  );
};

export default ForgotPasswordForm;
